/**
 * 编辑日程与提醒（与 iOS EventEditView 一致）：桌面端是居中的大对话框（两栏），手机上全屏。
 * 时间类型（全天 / 时段 / 截止 / 到点）+ 打开什么（不打开 / 学习内容 / 其它 App）+ 任务还是只提醒，docs/API.md 2.4。
 * 保存顺序：先 POST children/{cid}/events 或 PATCH events/{id}，再新建 / 修改 / 删除这个日程的提醒规则。
 */
import { CircleMinus, TabletSmartphone } from "lucide-react";
import { useMemo, useRef, useState, type ReactNode } from "react";
import {
  api,
  ApiError,
  appAction,
  confirm,
  dataStore,
  deleteEntity,
  offsetText,
  removeLocal,
  run,
  sessionStore,
  syncSoon,
  toast,
  useCalendar,
  useStore,
  writeEntity,
  WEEKDAY_NAMES,
  type Child,
  type EventAction,
} from "../../core";
import { Button, C, CATEGORY_KEYS, categoryStyle, ChoiceChip, DashedChip, EVENT_ICON_KEYS, FormCard, KPIcon, ModalHeader, NumberStepper, PillSegment, RowDivider, SectionLabel, Sheet, Switch, ValueRow } from "../../ui";
import {
  actionProblem,
  APP_PRESETS,
  canSave,
  changeAction,
  changeMode,
  changeStartDate,
  ESCALATE_CHOICES,
  endDateRange,
  eventBody,
  hasOnTimeBonus,
  initialForm,
  initialReminder,
  OFFSET_CHOICES,
  REPEAT_INTERVALS,
  REPEAT_MAXES,
  ruleBody,
  SOUNDS,
  spanHint,
  changeTask,
  TIME_MODE_CHOICES,
  timeProblem,
  validityText,
  wantsRule,
  type EventForm,
  type ReminderForm,
} from "./form";
import { AppPickerSheet, useAndroidTablets } from "./AppPickerSheet";
import { describeRepeat, REPEAT_KINDS, toggleDay, withKind } from "./repeat";

export interface EditTarget {
  eventId: string | null;
  date: string;
}

export function EventEditor({ child, target, onClose }: { child: Child; target: EditTarget; onClose: () => void }) {
  const cal = useCalendar();
  // 打开时取一次快照：编辑期间同步到的新数据不覆盖正在填的表单
  const [event] = useState(() => (target.eventId ? dataStore.getState().scheduleEvents[target.eventId] : undefined));
  const [existingRule] = useState(() => (event ? Object.values(dataStore.getState().reminderRules).find((r) => r.sourceType === "event" && r.sourceId === event.id) : undefined));
  const [form, setForm] = useState<EventForm>(() => initialForm(cal, event, target.date));
  const [rem, setRem] = useState<ReminderForm>(() => initialReminder(event, existingRule));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showValidity, setShowValidity] = useState(false);
  // 日程已保存但提醒保存失败时，再点保存改为 PATCH，不重复新建
  const savedId = useRef<string | null>(event?.id ?? null);
  const stillExists = useStore((s) => (event ? !!s.scheduleEvents[event.id] : true));

  const patch = (p: Partial<EventForm>) => setForm((f) => ({ ...f, ...p }));
  const patchRem = (p: Partial<ReminderForm>) => setRem((r) => ({ ...r, ...p }));

  const problem = timeProblem(cal, form);
  const hint = problem ? null : spanHint(cal, form);
  const [minEnd, maxEnd] = endDateRange(cal, form);
  const startWeekday = cal.weekday(form.startDate);
  const weekly = form.repeat.kind === "weekly" || form.repeat.kind === "biweekly";
  const catStyle = categoryStyle(form.category);
  const offsetChoices = useMemo(() => [...new Set<number>([...OFFSET_CHOICES, ...rem.offsets])].sort((a, b) => b - a), [rem.offsets]);

  function close() {
    // 确认对话框开着时按 Esc 只关确认框
    if (sessionStore.getState().confirm) return;
    onClose();
  }

  async function save() {
    if (!canSave(cal, form) || busy) return;
    setBusy(true);
    setError(null);
    try {
      const body = eventBody(cal, form);
      const id = savedId.current;
      const saved = id ? await writeEntity("scheduleEvents", "PATCH", `events/${id}`, body) : await writeEntity("scheduleEvents", "POST", `children/${child.id}/events`, body);
      savedId.current = saved.id;
      const existing = Object.values(dataStore.getState().reminderRules).find((r) => r.sourceType === "event" && r.sourceId === saved.id);
      if (wantsRule(rem)) {
        const rb = ruleBody(saved.id, rem);
        if (existing) await writeEntity("reminderRules", "PATCH", `reminder-rules/${existing.id}`, rb);
        else await writeEntity("reminderRules", "POST", `children/${child.id}/reminder-rules`, rb);
      } else if (existing) {
        await api.del(`reminder-rules/${existing.id}`);
        removeLocal("reminderRules", existing.id);
        syncSoon();
      }
      toast("已保存");
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.isUnauthorized) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!event) return;
    const ok = await confirm({
      title: `删除「${form.title.trim() || event.title}」？`,
      message: event.action?.type === "content" ? "所有重复的日子都会删除，还没做完的学习一起撤回（做完的记录保留）。" : "所有重复的日子都会删除。",
      confirmText: "删除日程和它的提醒",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    const done = await run(async () => {
      await deleteEntity("scheduleEvents", event.id, `events/${event.id}`);
      syncSoon();
      return true;
    }, "已删除");
    setBusy(false);
    if (done) onClose();
  }

  // ---------- 卡片 ----------

  const nameCard = (
    <div className="sch-ed-card">
      <label className="sch-ed-name">
        <span>名称</span>
        <input value={form.title} onChange={(e) => patch({ title: e.target.value })} placeholder="例如：钢琴课" maxLength={50} autoFocus={!event} name="title" />
      </label>
      <div className="sch-ed-cats" role="radiogroup" aria-label="类别">
        {CATEGORY_KEYS.map((k) => {
          const s = categoryStyle(k);
          const on = form.category === k;
          return (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={on}
              className={`sch-ed-cat ${on ? "is-on" : ""}`}
              style={{ background: on ? s.dot : s.tint, color: on ? "var(--kp-ink)" : s.ink }}
              onClick={() => patch({ category: k })}
            >
              {s.name}
            </button>
          );
        })}
      </div>
      <div className="sch-ed-icons" role="radiogroup" aria-label="图标">
        {EVENT_ICON_KEYS.map((k) => {
          const on = form.icon === k;
          return (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={k}
              className={`sch-ed-icon ${on ? "is-on" : ""}`}
              style={on ? { background: catStyle.tint, color: catStyle.ink, borderColor: catStyle.ink } : undefined}
              onClick={() => patch({ icon: k })}
            >
              <KPIcon name={k} size={18} strokeWidth={2.3} />
            </button>
          );
        })}
      </div>
    </div>
  );

  const allDay = form.mode === "all_day";
  const single = form.mode === "due" || form.mode === "point";
  const timeCard = (
    <FormCard>
      <div className="sch-ed-block">
        <PillSegment items={TIME_MODE_CHOICES} value={form.mode} onChange={(m) => setForm((f) => changeMode(cal, f, m))} height={34} stretch background={C.bg} />
        <p className="sch-ed-note">
          {form.mode === "all_day" ? "生日、放假、出游，可以选好几天" : form.mode === "range" ? "上课、兴趣班：有开始和结束，可以跨天" : form.mode === "due" ? "作业这类：几点前做完就行" : "喝水、收书包这类：到点提醒一下"}
        </p>
      </div>
      <RowDivider />
      {/* 全天只选日期；时段的结束可以选到后面几天（跨天）；截止 / 到点只有一个时间 */}
      <ValueRow title={allDay ? "开始日期" : form.mode === "due" ? "截止" : form.mode === "point" ? "时间" : "开始"}>
        <span className="sch-ed-dt">
          <input type="date" aria-label="开始日期" value={form.startDate} onChange={(e) => setForm((f) => changeStartDate(cal, f, e.target.value))} required />
          {!allDay && <input type="time" aria-label={form.mode === "due" ? "截止时间" : "开始时间"} value={form.startTime} onChange={(e) => e.target.value && patch({ startTime: e.target.value })} required />}
        </span>
      </ValueRow>
      {!single && (
        <>
          <RowDivider />
          <ValueRow title={allDay ? "结束日期" : "结束"}>
            <span className="sch-ed-dt">
              <input type="date" aria-label="结束日期" value={form.endDate} min={minEnd} max={maxEnd} onChange={(e) => e.target.value && patch({ endDate: e.target.value })} required />
              {!allDay && <input type="time" aria-label="结束时间" value={form.endTime} onChange={(e) => e.target.value && patch({ endTime: e.target.value })} required />}
            </span>
          </ValueRow>
        </>
      )}
      {problem ? (
        <p className="sch-ed-note is-error" role="alert">
          {problem}
        </p>
      ) : hint ? (
        <p className="sch-ed-note">{hint}</p>
      ) : null}
      <RowDivider />
      <div className="sch-ed-block">
        <div className="sch-ed-line">
          <span>重复</span>
          <span className="sch-ed-value">{form.repeat.kind === "none" ? "不重复" : describeRepeat(form.repeat, form.startDate)}</span>
        </div>
        <div className="sch-ed-chips">
          {REPEAT_KINDS.map(([k, label]) => (
            <ChoiceChip key={k} selected={form.repeat.kind === k} onClick={() => patch({ repeat: withKind(form.repeat, k, startWeekday) })}>
              {label}
            </ChoiceChip>
          ))}
        </div>
        {weekly && (
          <div className="sch-ed-weekdays" role="group" aria-label="星期">
            {[1, 2, 3, 4, 5, 6, 7].map((d) => {
              const on = form.repeat.days.includes(d);
              return (
                <button key={d} type="button" aria-pressed={on} style={on ? { background: catStyle.dot } : undefined} onClick={() => patch({ repeat: toggleDay(form.repeat, d) })}>
                  {WEEKDAY_NAMES[d]}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {form.repeat.kind !== "none" && (
        <>
          <RowDivider />
          <ValueRow title="有效期" value={validityText(cal, form)} onClick={() => setShowValidity(!showValidity)} height={44} />
          {showValidity && <ValidityPanel form={form} onChange={patch} />}
        </>
      )}
    </FormCard>
  );

  const actionCard = <ActionCard childId={child.id} action={form.action} onChange={(a) => setForm((f) => changeAction(f, a))} />;

  const extraCard = (
    <FormCard>
      <ValueRow title="地点">
        <input className="sch-ed-inline" value={form.location} onChange={(e) => patch({ location: e.target.value })} placeholder="可不填" maxLength={100} aria-label="地点" />
      </ValueRow>
      <RowDivider />
      <ValueRow title="备注">
        <input className="sch-ed-inline" value={form.notes} onChange={(e) => patch({ notes: e.target.value })} placeholder="例如：带上琴谱" maxLength={200} aria-label="备注" />
      </ValueRow>
      <RowDivider />
      {/* 任务：孩子在平板上点"做完了"（学习内容是做完学习），家长确认后加分；只提醒：到点提醒一下，不用做完 */}
      <Switch label="要孩子做完" checked={form.task} disabled={form.action?.type === "content"} onChange={(v) => setForm((f) => changeTask(f, v))} />
      <p className="sch-ed-note">
        {form.action?.type === "content" ? "学习内容一定是任务，做完后你确认加分" : form.task ? "任务：孩子点“做完了”，你确认后加分" : "只提醒：到点提醒一下，不用点做完了、不加分（放学、上课这类）"}
      </p>
      {form.task && (
        <>
          <RowDivider />
          <ValueRow title="完成奖励" height={56}>
            <NumberStepper value={form.points} min={0} max={100} onChange={(v) => patch({ points: v })} />
          </ValueRow>
          {hasOnTimeBonus(form) && (
            <>
              <RowDivider />
              <ValueRow title="按时完成另加" height={56}>
                <NumberStepper value={form.bonus} min={0} max={100} onChange={(v) => patch({ bonus: v })} />
              </ValueRow>
            </>
          )}
        </>
      )}
    </FormCard>
  );

  const reminderCard = (
    <FormCard>
      <div className="sch-ed-block">
        <div className="sch-ed-line">
          <span>提前多久</span>
          <span className="sch-ed-value">可多选</span>
        </div>
        <div className="sch-ed-chips">
          {offsetChoices.map((m) => {
            const on = rem.offsets.includes(m);
            return (
              <ChoiceChip key={m} selected={on} onClick={() => patchRem({ offsets: on ? rem.offsets.filter((x) => x !== m) : [...rem.offsets, m] })}>
                {offsetText(m)}
              </ChoiceChip>
            );
          })}
        </div>
        {rem.offsets.length === 0 && <p className="sch-ed-note is-error">至少选一个时间，否则不会提醒</p>}
      </div>
      <RowDivider />
      <div className="sch-ed-block">
        <div className="sch-ed-line">
          <span>播报文案</span>
        </div>
        <textarea className="sch-ed-speak" rows={2} value={rem.speakText} onChange={(e) => patchRem({ speakText: e.target.value })} placeholder="{title}要开始啦" maxLength={200} aria-label="播报文案" />
        <p className="sch-ed-note">
          {allDay ? "「{title}」会换成日程名称；全天日程的提醒以当天 8:00 为准" : form.mode === "due" ? "「{title}」会换成日程名称；提前多久是相对截止时间" : "「{title}」会换成日程名称"}
        </p>
      </div>
      <RowDivider />
      <div className="sch-ed-block">
        <div className="sch-ed-line">
          <span>铃声</span>
        </div>
        <div className="sch-ed-chips">
          {SOUNDS.map(([k, label]) => (
            <ChoiceChip key={k} selected={rem.sound === k} onClick={() => patchRem({ sound: k })}>
              {label}
            </ChoiceChip>
          ))}
          {!SOUNDS.some(([k]) => k === rem.sound) && (
            <ChoiceChip selected onClick={() => undefined}>
              自定义
            </ChoiceChip>
          )}
        </div>
      </div>
      <RowDivider />
      <Switch label={"需要孩子点“知道了”"} checked={rem.requireAck} onChange={(v) => patchRem({ requireAck: v })} />
      {rem.requireAck && (
        <>
          <RowDivider />
          <ValueRow title="没确认就重复">
            <span className="sch-ed-selects">
              <Select value={rem.repeatInterval} onChange={(v) => patchRem({ repeatInterval: v })} disabled={rem.repeatMax === 0} label="重复间隔">
                {REPEAT_INTERVALS.map((m) => (
                  <option key={m} value={m}>
                    每 {m} 分钟
                  </option>
                ))}
              </Select>
              <Select value={rem.repeatMax} onChange={(v) => patchRem({ repeatMax: v })} label="重复次数">
                {REPEAT_MAXES.map((n) => (
                  <option key={n} value={n}>
                    {n === 0 ? "不重复" : `最多 ${n} 次`}
                  </option>
                ))}
              </Select>
            </span>
          </ValueRow>
          <RowDivider />
          <ValueRow title="超时通知我">
            <Select value={rem.escalate ?? 0} onChange={(v) => patchRem({ escalate: v === 0 ? null : v })} label="超时通知我">
              <option value={0}>不通知</option>
              {[...new Set<number>([...ESCALATE_CHOICES, ...(rem.escalate ? [rem.escalate] : [])])]
                .sort((a, b) => a - b)
                .map((m) => (
                  <option key={m} value={m}>
                    {m} 分钟没确认
                  </option>
                ))}
            </Select>
          </ValueRow>
        </>
      )}
    </FormCard>
  );

  return (
    <Sheet open onClose={close} width={900} persistent label={event ? "编辑日程" : "新建日程"}>
      <div className="sch-editor">
        <ModalHeader title={event ? "编辑日程" : "新建日程"} actionTitle="保存" actionEnabled={canSave(cal, form) && stillExists} busy={busy} onCancel={close} onAction={() => void save()} />
        <div className="sch-editor-body">
          {!stillExists && <p className="sch-ed-note is-error">这个日程已经被删除了</p>}
          <div className="sch-editor-cols">
            <div className="sch-editor-col">
              {nameCard}
              {timeCard}
              {actionCard}
              {extraCard}
            </div>
            <div className="sch-editor-col">
              <SectionLabel action={<Switch checked={rem.on} onChange={(v) => patchRem({ on: v })} />}>提醒</SectionLabel>
              {rem.on ? reminderCard : <p className="sch-ed-off">关闭后到时间不会提醒孩子</p>}
              {error && (
                <p className="sch-ed-note is-error sch-ed-error" role="alert">
                  {error}
                </p>
              )}
              {event && stillExists && (
                <Button variant="soft" block className="sch-ed-delete" onClick={() => void remove()} disabled={busy}>
                  删除这个日程
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Sheet>
  );
}

/** 打开什么：不打开 / App 里的学习内容 / 其它 App（常用列表 + 自定义；孩子有安卓平板时可以从平板上选） */
function ActionCard({ childId, action, onChange }: { childId: string; action: EventAction | null; onChange: (a: EventAction | null) => void }) {
  const contents = useStore((s) => s.contents);
  const list = useMemo(() => Object.values(contents).filter((c) => !c.deletedAt).sort((a, b) => a.title.localeCompare(b.title, "zh")), [contents]);
  const type = action?.type ?? "none";
  const app = action?.type === "app" ? action : null;
  const preset = app ? APP_PRESETS.find((p) => p.app === app.app) : undefined;
  const [custom, setCustom] = useState(() => !!app && !preset);
  const hasTablet = useAndroidTablets(childId).length > 0;
  const [picking, setPicking] = useState(false);
  const problem = actionProblem(action);
  const switchType = (t: "none" | "content" | "app") => {
    if (t === type) return;
    if (t === "none") onChange(null);
    else if (t === "content") onChange({ type: "content", contentId: "" });
    else {
      const p = APP_PRESETS[0]!;
      setCustom(false);
      onChange({ type: "app", ...p });
    }
  };
  return (
    <FormCard>
      <div className="sch-ed-block">
        <div className="sch-ed-line">
          <span>打开</span>
          <span className="sch-ed-value">孩子点日程时打开</span>
        </div>
        <PillSegment
          items={[["none", "不打开"], ["content", "学习内容"], ["app", "其它 App"]] as const}
          value={type}
          onChange={switchType}
          height={34}
          stretch
          background={C.bg}
        />
        {action?.type === "content" && (
          <>
            <select className="sch-select sch-ed-content" aria-label="学习内容" value={action.contentId} onChange={(e) => onChange({ type: "content", contentId: e.target.value })}>
              <option value="">选一个学习内容…</option>
              {list.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
            <p className="sch-ed-note">每次发生生成一条学习记录，孩子点"去完成"打开，做完后家长确认计分</p>
          </>
        )}
        {app && (
          <>
            <div className="sch-ed-chips">
              {APP_PRESETS.map((p) => (
                <ChoiceChip
                  key={p.app}
                  selected={!custom && app.app === p.app}
                  onClick={() => {
                    setCustom(false);
                    onChange({ type: "app", ...p });
                  }}
                >
                  {p.name}
                </ChoiceChip>
              ))}
              <ChoiceChip
                selected={custom}
                onClick={() => {
                  setCustom(true);
                  onChange({ type: "app", name: "", app: null, iosUrl: "", androidPackage: "" });
                }}
              >
                自定义
              </ChoiceChip>
              {hasTablet && (
                <DashedChip onClick={() => setPicking(true)}>
                  <TabletSmartphone size={14} strokeWidth={2.4} />
                  从平板上选
                </DashedChip>
              )}
            </div>
            {custom && (
              <div className="sch-ed-app">
                <input className="sch-ed-inline" value={app.name} onChange={(e) => onChange({ ...app, name: e.target.value })} placeholder="App 名称，如 小猿口算" maxLength={30} aria-label="App 名称" />
                <input className="sch-ed-inline" value={app.iosUrl ?? ""} onChange={(e) => onChange({ ...app, iosUrl: e.target.value })} placeholder="iPad 链接，如 weixin://" maxLength={300} aria-label="iPad 链接" />
                <input className="sch-ed-inline" value={app.androidPackage ?? ""} onChange={(e) => onChange({ ...app, androidPackage: e.target.value })} placeholder="安卓包名，如 com.tencent.mm" maxLength={200} aria-label="安卓包名" />
              </div>
            )}
            <p className="sch-ed-note">孩子点"打开"进入这个 App</p>
          </>
        )}
        {problem && type !== "none" && <p className="sch-ed-note is-error">{problem}</p>}
      </div>
      <AppPickerSheet
        open={picking}
        childId={childId}
        onClose={() => setPicking(false)}
        onPick={(a) => {
          // 选好后显示自定义的输入框（名称、安卓包名已填好，iPad 链接留空）
          setCustom(true);
          onChange(appAction(a));
        }}
      />
    </FormCard>
  );
}

function Select({ value, onChange, children, disabled, label }: { value: number; onChange: (v: number) => void; children: ReactNode; disabled?: boolean; label: string }) {
  return (
    <select className="sch-select" value={value} disabled={disabled} aria-label={label} onChange={(e) => onChange(Number(e.target.value))}>
      {children}
    </select>
  );
}

/** 有效期：截止日期 + 例外日期（iOS 是单独的弹出页，网页版在表单里展开） */
function ValidityPanel({ form, onChange }: { form: EventForm; onChange: (p: Partial<EventForm>) => void }) {
  const cal = useCalendar();
  const [newEx, setNewEx] = useState(() => (form.startDate > cal.today() ? form.startDate : cal.today()));
  const sorted = [...form.exdates].sort();
  const add = () => {
    if (newEx && !form.exdates.includes(newEx)) onChange({ exdates: [...form.exdates, newEx] });
  };
  return (
    <div className="sch-ed-validity">
      <Switch label="设置结束日期" checked={form.untilDate != null} onChange={(on) => onChange({ untilDate: on ? cal.addDays(form.startDate, 90) : null })} />
      {form.untilDate != null && (
        <ValueRow title="最后一天">
          <input type="date" className="sch-ed-date" aria-label="最后一天" value={form.untilDate} min={form.startDate} onChange={(e) => e.target.value && onChange({ untilDate: e.target.value })} />
        </ValueRow>
      )}
      <p className="sch-ed-sub">例外日期（这些天不发生，比如放假）</p>
      {sorted.map((d) => (
        <div key={d} className="sch-ed-ex">
          <span>
            {cal.monthDay(d)} {cal.weekdayName(d)}
          </span>
          <button type="button" aria-label={`去掉 ${cal.monthDay(d)}`} title="去掉" onClick={() => onChange({ exdates: form.exdates.filter((x) => x !== d) })}>
            <CircleMinus size={18} color="var(--kp-danger)" />
          </button>
        </div>
      ))}
      <div className="sch-ed-ex-add">
        <input type="date" className="sch-ed-date" aria-label="例外日期" value={newEx} onChange={(e) => setNewEx(e.target.value)} />
        <Button variant="soft" height={36} onClick={add} disabled={!newEx || form.exdates.includes(newEx)}>
          添加例外
        </Button>
      </div>
    </div>
  );
}
